import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

// The bands of a region lens, called out down the right-hand margin.
//
// THE PLACEMENT IS MEASURED, NOT ASSUMED. This used to space the callouts by a
// fixed twenty percent of the rail and hope they fitted. They did not: a
// three-line note makes a box 81 to 137 pixels tall, twenty percent of the rail
// is frequently less than that, and the Four Worlds overlapped in pairs at two
// of the three breakpoints on both trees. Percentages cannot know how tall a
// paragraph is. So the boxes are rendered, measured, and then placed.
//
// Same rule as the label layer: measure it, do not eyeball it — and when the
// content will not fit, drop content rather than shaving pixels.

const GAP = 10; // px between boxes; below this they read as one block
const EDGE = 4; // px of breathing room at the top and bottom of the rail

function bandCentre(group, sephirothBySlug) {
  const ys = group.items
    .filter((item) => item.kind === "sephirah")
    .map((item) => sephirothBySlug.get(item.slug)?.y)
    .filter((y) => Number.isFinite(y));
  if (ys.length === 0) return null;
  return (Math.min(...ys) + Math.max(...ys)) / 2;
}

// Place boxes of KNOWN height as near their band as they can get without
// touching. Sweep down resolving overlaps, then sweep back up if the last one
// has been pushed off the bottom. Two passes settle it because the ordering is
// fixed: a band never has to overtake the band above it.
function solve(wanted, heights, railHeight) {
  const tops = wanted.map((centre, i) => centre - heights[i] / 2);

  tops[0] = Math.max(tops[0], EDGE);
  for (let i = 1; i < tops.length; i += 1) {
    tops[i] = Math.max(tops[i], tops[i - 1] + heights[i - 1] + GAP);
  }

  const last = tops.length - 1;
  const bottom = railHeight - EDGE;
  if (tops[last] + heights[last] > bottom) {
    tops[last] = bottom - heights[last];
    for (let i = last - 1; i >= 0; i -= 1) {
      tops[i] = Math.min(tops[i], tops[i + 1] - heights[i] - GAP);
    }
  }
  return tops;
}

export default function RegionCallouts({
  overlay,
  tradition,
  viewBox,
  sephirothBySlug,
  selected = null,
  onSelect = null
}) {
  const railRef = useRef(null);
  const boxRefs = useRef(new Map());
  // null until measured. Rendering at the band centre first and correcting on
  // the layout effect means no flash of overlapping boxes: the effect runs
  // before paint.
  const [tops, setTops] = useState(null);
  // Set only when the boxes genuinely cannot fit the rail. Removing the note
  // line is removing a label, which is the project's answer to running out of
  // room; shrinking the type would be shaving pixels.
  const [compact, setCompact] = useState(false);

  const groups = useMemo(() => {
    if (!overlay) return [];
    return overlay.groups.map((group) => {
      const y = bandCentre(group, sephirothBySlug);
      const fraction = y === null ? 0.5 : (y - viewBox.y) / viewBox.height;
      return { ...group, fraction: Math.min(Math.max(fraction, 0), 1) };
    });
  }, [overlay, sephirothBySlug, viewBox]);

  const place = useCallback(() => {
    const rail = railRef.current;
    if (!rail || groups.length === 0) return;

    const railHeight = rail.clientHeight;
    const heights = groups.map((group) => boxRefs.current.get(group.slug)?.offsetHeight ?? 0);
    if (railHeight === 0 || heights.some((height) => height === 0)) return;

    const needed = heights.reduce((sum, h) => sum + h, 0) + GAP * (heights.length - 1);
    if (needed > railHeight - EDGE * 2) {
      // Try again without the notes. If it still does not fit compact, the
      // solver below will simply run off the bottom, and that is visible —
      // better than silently stacking boxes on top of each other.
      if (!compact) {
        setCompact(true);
        return;
      }
    }

    const wanted = groups.map((group) => group.fraction * railHeight);
    const next = solve(wanted, heights, railHeight);
    setTops((current) =>
      current && current.length === next.length &&
      current.every((value, i) => Math.abs(value - next[i]) < 0.5)
        ? current
        : next
    );
  }, [groups, compact]);

  useLayoutEffect(place, [place]);

  // The observer has to call the CURRENT place, not the one that existed when
  // it was created — and it has to call it, not merely clear the answer.
  // Clearing `tops` alone left the boxes wherever they were: nothing in the
  // dependency list had changed, so the layout effect never re-ran and the
  // largest breakpoint kept an overlap the smaller ones did not have.
  const placeRef = useRef(place);
  placeRef.current = place;

  useEffect(() => {
    const rail = railRef.current;
    if (!rail || typeof ResizeObserver !== "function") return undefined;
    const observer = new ResizeObserver(() => {
      // A rail that has grown may have room for the notes again, so compact is
      // re-decided from scratch rather than sticking for the session.
      setCompact(false);
      placeRef.current();
    });
    observer.observe(rail);
    return () => observer.disconnect();
  }, []);

  if (!overlay) return null;

  const select = (group) => {
    if (!onSelect) return;
    // Selecting the same band twice closes it, the way the spheres behave.
    const isOpen = selected && selected.type === "marker" && selected.slug === group.slug;
    onSelect(isOpen ? null : { type: "marker", slug: group.slug });
  };

  return (
    <aside
      className="region-callouts"
      ref={railRef}
      aria-label={`${overlay.name} regions`}
      data-testid={`region-callouts-${overlay.slug}`}
      data-compact={compact ? "true" : "false"}
    >
      <h2 className="visually-hidden">{overlay.name}</h2>
      <svg
        className="region-callouts__connectors"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {groups.map((group, index) => {
          const railHeight = railRef.current?.clientHeight || 0;
          const sourceY = group.fraction * 100;
          const box = boxRefs.current.get(group.slug);
          const labelY =
            tops && railHeight
              ? ((tops[index] + (box?.offsetHeight ?? 0) / 2) / railHeight) * 100
              : sourceY;
          return (
            <line
              key={group.slug}
              x1="0"
              y1={sourceY}
              x2="10"
              y2={labelY}
              stroke={group.colorHex || "#E8E3D9"}
              data-testid={`region-connector-${group.slug}`}
            />
          );
        })}
      </svg>

      {groups.map((group, index) => {
        const open = Boolean(selected && selected.type === "marker" && selected.slug === group.slug);
        return (
          <button
            type="button"
            className={open ? "region-callout region-callout--on" : "region-callout"}
            key={group.slug}
            ref={(node) => {
              if (node) boxRefs.current.set(group.slug, node);
              else boxRefs.current.delete(group.slug);
            }}
            style={{
              // Before the measurement lands, fall back to the band itself.
              top: tops ? `${tops[index]}px` : `${group.fraction * 100}%`,
              transform: tops ? "none" : "translateY(-50%)",
              "--region-color": group.colorHex || "#E8E3D9"
            }}
            aria-pressed={open}
            onClick={() => select(group)}
            data-testid={`region-callout-${group.slug}`}
          >
            <span className="region-callout__name">{group.name}</span>
            {group.note && !compact && (
              <span className="region-callout__note">{group.note}</span>
            )}
          </button>
        );
      })}
    </aside>
  );
}
